// Wspólne odtwarzanie alarmów dla minutników i Pomodoro: lista dźwięków,
// zapętlanie z narastaniem głośności i dogranie po pierwszym kliknięciu,
// gdy przeglądarka zablokuje autoplay.
//
// Kanały ('timers', 'pomodoro') są niezależne — dzwoniący minutnik nie ucichnie
// od tego, że Pomodoro zaczęło własny sygnał.
import { t } from '@/i18n/index.js'

export interface AlarmSound {
    key: string
    label: string
    src: string
    gentle: boolean
}

// Nazwa dźwięku z tools.sounds.<klucz> — getter, bo język zmienia się w trakcie działania.
const sound = (def: Omit<AlarmSound, 'label'>): AlarmSound =>
    Object.defineProperty(def, 'label', { get: () => t(`tools.sounds.${def.key}`), enumerable: true }) as AlarmSound

export const ALARM_SOUNDS: AlarmSound[] = [
    sound({ key: 'gong', src: '/alarms/gong.wav', gentle: true }),
    sound({ key: 'chimes', src: '/alarms/chimes.wav', gentle: true }),
    sound({ key: 'marimba', src: '/alarms/marimba.wav', gentle: true }),
    sound({ key: 'drop', src: '/alarms/drop.wav', gentle: true }),
    sound({ key: 'pulse', src: '/alarms/pulse.wav', gentle: false }),
    sound({ key: 'beep', src: '/alarms/beep.wav', gentle: false }),
    sound({ key: 'alarm_1', src: '/alarms/alarm_1.wav', gentle: false }),
    sound({ key: 'alarm_2', src: '/alarms/alarm_2.wav', gentle: false }),
]

// Do list rozwijanych: łagodne osobno od wyraźnych.
export const ALARM_SOUND_GROUPS = [
    { get label() { return t('tools.sounds.gentle') }, items: ALARM_SOUNDS.filter(s => s.gentle) },
    { get label() { return t('tools.sounds.loud') }, items: ALARM_SOUNDS.filter(s => !s.gentle) },
]

export const alarmSoundLabel = (key: string): string =>
    ALARM_SOUNDS.find(s => s.key === key)?.label ?? ALARM_SOUNDS[0].label

export const alarmSoundSrc = (key: string): string =>
    (ALARM_SOUNDS.find(s => s.key === key) ?? ALARM_SOUNDS[0]).src

export const isGentleSound = (key: string): boolean =>
    ALARM_SOUNDS.find(s => s.key === key)?.gentle ?? false

// Zapętlony alarm startuje cicho i dochodzi do pełnej głośności w ~30 s.
const RAMP_START_VOLUME = 0.35
const RAMP_SECONDS = 30
const RAMP_STEP_MS = 1000

interface AlarmChannel {
    alarm: HTMLAudioElement | null
    test: HTMLAudioElement | null
    rampId: any
    interaction: (() => void) | null
}

const channels = new Map<string, AlarmChannel>()

const channelFor = (name: string): AlarmChannel => {
    if (!channels.has(name)) channels.set(name, { alarm: null, test: null, rampId: null, interaction: null })
    return channels.get(name)!
}

// isRinging: czy alarm nadal ma prawo grać (np. minutnik wciąż w stanie "finished").
export const useAlarmSound = (name: string, isRinging: () => boolean = () => true) => {
    const ch = channelFor(name)

    const clearRamp = () => {
        if (!ch.rampId) return
        clearInterval(ch.rampId)
        ch.rampId = null
    }

    const removeInteractionHandler = () => {
        if (!ch.interaction) return
        window.removeEventListener('pointerdown', ch.interaction)
        window.removeEventListener('keydown', ch.interaction)
        window.removeEventListener('touchstart', ch.interaction)
        ch.interaction = null
    }

    // Autoplay zablokowany — dogrywamy przy pierwszej interakcji użytkownika.
    const armPlayOnInteraction = () => {
        if (!import.meta.client || ch.interaction) return
        ch.interaction = () => {
            if (ch.alarm && isRinging()) ch.alarm.play().catch(() => {})
            removeInteractionHandler()
        }
        window.addEventListener('pointerdown', ch.interaction)
        window.addEventListener('keydown', ch.interaction)
        window.addEventListener('touchstart', ch.interaction)
    }

    const stopTest = () => {
        if (!ch.test) return
        ch.test.pause()
        ch.test.currentTime = 0
        ch.test = null
    }

    const stop = () => {
        clearRamp()
        if (ch.alarm) {
            ch.alarm.pause()
            ch.alarm.currentTime = 0
            ch.alarm = null
        }
        removeInteractionHandler()
    }

    const play = (src: string, loop: boolean) => {
        if (!import.meta.client) return
        stopTest()
        stop()

        const audio = new Audio(src)
        audio.loop = loop
        audio.volume = loop ? RAMP_START_VOLUME : 1
        ch.alarm = audio

        const p = audio.play()
        if (p && typeof p.catch === 'function') p.catch(() => { if (loop) armPlayOnInteraction() })

        if (!loop) return

        const step = (1 - RAMP_START_VOLUME) / RAMP_SECONDS
        ch.rampId = setInterval(() => {
            if (!ch.alarm) { clearRamp(); return }
            ch.alarm.volume = Math.min(1, ch.alarm.volume + step)
            if (ch.alarm.volume >= 1) clearRamp()
        }, RAMP_STEP_MS)
    }

    // Podsłuch dźwięku w ustawieniach — zawsze pełna głośność, bez pętli.
    const playTest = (src: string) => {
        if (!import.meta.client) return
        stopTest()
        ch.test = new Audio(src)
        ch.test.play().catch(() => {})
    }

    // Głośność bieżącego alarmu (0–1) — do podglądu narastania.
    const volume = () => ch.alarm?.volume ?? 0

    return { play, stop, playTest, stopTest, volume }
}
